/**
 * Referral tracking utilities
 */

const REFERRAL_STORAGE_KEY = 'fairday_referral';

/**
 * Generate a short referral code from user ID
 * Uses base36 encoding for compact codes
 */
export function generateReferralCode(userId: string): string {
  if (!userId) return '';

  // Create a simple hash from user ID and encode in base36
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Convert to positive number and encode
  const positiveHash = Math.abs(hash);
  return positiveHash.toString(36).toUpperCase().slice(0, 8);
}

/**
 * Build a share URL with referral code
 */
export function buildShareUrl(baseUrl: string, referralCode?: string): string {
  if (!referralCode) return baseUrl;

  const url = new URL(baseUrl);
  url.searchParams.set('ref', referralCode);
  return url.toString();
}

/**
 * Extract referral code from current URL
 */
export function getReferralFromUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  return params.get('ref');
}

/**
 * Store referral code for attribution
 */
export function storeReferral(referralCode: string): void {
  if (!referralCode || typeof localStorage === 'undefined') return;

  // Only store if we don't already have one (first touch attribution)
  const existing = localStorage.getItem(REFERRAL_STORAGE_KEY);
  if (!existing) {
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify({
      code: referralCode,
      timestamp: Date.now(),
      landingPage: window.location.pathname,
    }));
  }
}

/**
 * Get stored referral data
 */
export function getStoredReferral(): { code: string; timestamp: number; landingPage: string } | null {
  if (typeof localStorage === 'undefined') return null;

  const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Clear stored referral (e.g., after successful attribution)
 */
export function clearStoredReferral(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(REFERRAL_STORAGE_KEY);
}

/**
 * Initialize referral tracking - call on app load
 */
export function initReferralTracking(): void {
  const refCode = getReferralFromUrl();
  if (refCode) {
    storeReferral(refCode);

    // Clean URL without losing navigation state
    if (typeof window !== 'undefined' && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
    }
  }
}
